'use client';

import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

import { IconWrap } from './header.elements';

import { subscribeUser } from '@/helpers/pwa-actions.helper';
import { urlBase64ToUint8Array } from '@/utils/functions.utils';
import notificationAllowInstruction from '@/assets/img/notification_allow_instruction.jpg';

import { RiMobileDownloadLine } from 'react-icons/ri';

const PwaInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    // Debug logging for PWA install
    console.log('[PWA] Component mounted, setting up install prompt listener');

    // Register service worker early - required for beforeinstallprompt on Android
    const registerSW = async () => {
      if ('serviceWorker' in navigator) {
        try {
          await navigator.serviceWorker.register('/sw.js', {
            scope: '/',
            updateViaCache: 'none',
          });
        } catch (error) {
          console.warn('[PWA] SW registration failed:', error);
        }
      } else {
        console.warn('[PWA] Service workers not supported');
      }
    };

    registerSW();

    const handler = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();

      // Save the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if already installed (standalone mode or iOS)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    if (isStandalone) {
      setIsInstallable(false);
    }

    // Hide button when app is installed
    const handleAppInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const registerServiceWorker = async () => {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    return registration;
  };

  const subscribeToPushNotifications = async () => {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY),
    });
    const serializedSub = JSON.parse(JSON.stringify(sub));
    await subscribeUser(serializedSub);
  };

  const handlePushNotificationPrompt = async () => {
    // Check if push notifications are supported
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return;
    }

    // Check if notifications are already enabled
    if (Notification.permission === 'granted') {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        // Already subscribed, no need to prompt
        return;
      }
    }

    const result = await Swal.fire({
      title: 'Omogući obavijesti?',
      html: 'Želite li primati push obavijesti o novostima i događajima?',
      showCancelButton: true,
      confirmButtonText: 'Omogući',
      cancelButtonText: 'Kasnije',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      position: 'top',
    });

    if (!result.isConfirmed) return;

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        await Swal.fire({
          title: 'Obavijesti blokirane',
          html: `
            <p>Morate omogućiti obavijesti u postavkama preglednika.</p>
            <img src="${notificationAllowInstruction.src}" style="width: 100%; margin-top: 10px;" alt="Kako omogućiti obavijesti" />
          `,
          icon: 'warning',
          confirmButtonText: 'U redu',
          position: 'top',
        });
        return;
      }

      // Register service worker and subscribe to push
      await registerServiceWorker();
      await subscribeToPushNotifications();

      toast.success('Obavijesti omogućene.', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
    } catch {
      toast.error('Došlo je do greške prilikom omogućavanja obavijesti.', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
      });
    }
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    const result = await Swal.fire({
      title: 'Želite li instalirati Izvrsnost?',
      html: 'Instalirajte Izvrsnost aplikaciju za brži pristup i primanje pravovremenih obavijesti na koje se pretplatite.',
      showCancelButton: true,
      confirmButtonText: 'Instaliraj',
      cancelButtonText: 'Odustani',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      position: 'top',
    });

    if (!result.isConfirmed) return;

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    console.log(`User response: ${outcome}`);

    // Clear the saved prompt since it can't be used again
    setDeferredPrompt(null);
    setIsInstallable(false);

    // If the user accepted the install, prompt for push notifications
    if (outcome === 'accepted') {
      // Small delay to let the install complete
      setTimeout(() => {
        handlePushNotificationPrompt();
      }, 1000);
    }
  };

  // Don't show button if not installable
  if (!isInstallable) return null;

  return (
    <IconWrap onClick={handleInstallClick}>
      <RiMobileDownloadLine />
    </IconWrap>
  );
};

export { PwaInstallButton };
